import type { MatchEvent, MatchEventType } from '../api';

const ICONS: Record<MatchEventType, string> = {
  'goal': '⚽',
  'own-goal': '⚽⚠',
  'penalty-goal': '⚽🅿',
  'penalty-miss': '✗🅿',
  'yellow-card': '🟨',
  'red-card': '🟥',
  'yellow-red-card': '🟨🟥',
  'substitution': '↔',
  'var': '📺',
  'kickoff': '▶',
  'half-end': '⏸',
  'full-time': '⏹',
  'other': '·',
};

const CARRIES_SCORE_DELTA: Record<string, boolean> = {
  'goal': true, 'own-goal': true, 'penalty-goal': true,
};

export default function EventTimeline({ events, homeName, awayName }: {
  events: MatchEvent[]; homeName: string; awayName: string;
}) {
  const filtered = events.filter((e) => e.type !== 'kickoff' && e.type !== 'other' && e.teamSide !== null);
  if (filtered.length === 0) return null;

  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-xs uppercase tracking-widest text-dim">Match events</h2>
        <span className="text-[10px] text-dim">{filtered.length} events</span>
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] gap-x-2 gap-y-1.5 items-baseline">
        <div className="text-[10px] uppercase tracking-wider text-dim text-right truncate">{homeName}</div>
        <div className="text-[10px] uppercase tracking-wider text-dim text-center">Min</div>
        <div className="text-[10px] uppercase tracking-wider text-dim truncate">{awayName}</div>
        {filtered.map((e) => (
          <EventRow key={e.id} ev={e} />
        ))}
      </div>
    </section>
  );
}

function EventRow({ ev }: { ev: MatchEvent }) {
  const icon = ICONS[ev.type] ?? '·';
  const players = ev.participants.map((p) => p.name).filter(Boolean);
  const isHome = ev.teamSide === 'home';
  const isAway = ev.teamSide === 'away';
  const accent = ev.scoringPlay || CARRIES_SCORE_DELTA[ev.type]
    ? 'text-pitch-400 font-semibold'
    : ev.type === 'red-card' || ev.type === 'yellow-red-card'
      ? 'text-red-400'
      : 'text-chalk';

  const content = (
    <div className="text-sm">
      <span className="mr-1.5">{icon}</span>
      {ev.type === 'substitution' && players.length === 2 ? (
        <span className={accent}>
          <span>{players[0]}</span>
          <span className="text-dim text-xs"> in · </span>
          <span className="text-dim">{players[1]} out</span>
        </span>
      ) : (
        <span className={accent}>{players[0] || ev.shortText || ev.typeText}</span>
      )}
    </div>
  );

  return (
    <>
      <div className="text-right">{isHome ? content : null}</div>
      <div className="text-[10px] text-dim font-mono text-center px-1">{ev.minute || '—'}</div>
      <div className="text-left">{isAway ? content : null}</div>
    </>
  );
}
